import React, { useState, useContext, useEffect } from "react";
import { toast } from "sonner";
import { add, format, parseISO } from "date-fns";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { socketConnection } from "../../services/socket";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
} from "@material-ui/core";
import {
  Edit,
  CheckCircle,
  SignalCellularConnectedNoInternet2Bar,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  CropFree,
  DeleteOutline,
  Facebook,
  Instagram,
  WhatsApp,
  SettingsOutlined,
  ArrowDropDown,
  RefreshOutlined,
  HeadsetMicOutlined,

  PhoneAndroid,
  Schedule,
  StarOutlined,
} from "@material-ui/icons";

import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import formatSerializedId from "../../utils/formatSerializedId";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import Skeleton from "@material-ui/lab/Skeleton";

const useStyles = makeStyles((theme) => ({
  // --- Options dropdown ---
  optionsButton: {
    borderRadius: 10,
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    fontSize: 14,
    padding: "8px 16px",
    color: theme.palette.type === "dark" ? "#E2E8F0" : "#1E293B",
    backgroundColor: "transparent",
    boxShadow: "none",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.12)"
      : "1px solid #E2E8F0",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "#F8FAFC",
      boxShadow: "none",
    },
  },
  optionsMenu: {
    "& .MuiPaper-root": {
      borderRadius: 12,
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      border:
        theme.palette.type === "dark"
          ? "1px solid rgba(255,255,255,0.08)"
          : "1px solid #E2E8F0",
      minWidth: 220,
    },
    "& .MuiMenuItem-root": {
      fontFamily: '"Inter", "Poppins", sans-serif',
      fontSize: 14,
      padding: "10px 16px",
    },
    "& .MuiListItemIcon-root": {
      minWidth: 32,
    },
  },
  menuSectionLabel: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: theme.palette.type === "dark" ? "#64748B" : "#94A3B8",
    padding: "10px 16px 4px",
    cursor: "default",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  // --- Card grid ---
  gridContainer: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2, 3, 1.5),
    ...theme.scrollbarStyles,
  },
  connectionCard: {
    borderRadius: 12,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid #E2E8F0",
    boxShadow: "none",
    transition: "all 0.2s ease",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      boxShadow:
        theme.palette.type === "dark"
          ? "0 4px 16px rgba(0,0,0,0.3)"
          : "0 4px 16px rgba(0,0,0,0.06)",
      borderColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.12)"
          : "#CBD5E1",
    },
  },
  cardContent: {
    padding: "20px !important",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    flex: 1,
  },

  // Card header
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: 1,
    minWidth: 0,
  },
  channelIconWrapper: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  channelIconWhatsapp: {
    backgroundColor: "rgba(37, 211, 102, 0.1)",
    color: "#25d366",
  },
  channelIconFacebook: {
    backgroundColor: "rgba(59, 89, 152, 0.1)",
    color: "#3b5998",
  },
  channelIconInstagram: {
    backgroundColor: "rgba(225, 48, 108, 0.1)",
    color: "#e1306c",
  },
  connectionName: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 15,
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  defaultBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 600,
    color: green[600],
    backgroundColor: "rgba(34, 197, 94, 0.08)",
    borderRadius: 6,
    padding: "2px 6px",
    whiteSpace: "nowrap",
  },

  // Card body
  cardBody: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  infoIcon: {
    fontSize: 16,
    color: theme.palette.type === "dark" ? "#64748B" : "#94A3B8",
    flexShrink: 0,
  },
  infoLabel: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 13,
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    flexShrink: 0,
  },
  infoValue: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#E2E8F0" : "#334155",
    marginLeft: "auto",
  },

  // Status chip
  statusChip: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 11,
    fontWeight: 600,
    height: 24,
    borderRadius: 6,
    "& .MuiChip-label": {
      padding: "0 8px",
    },
  },
  statusConnected: {
    backgroundColor: "rgba(34, 197, 94, 0.1)",
    color: "#16A34A",
  },
  statusDisconnected: {
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    color: "#DC2626",
  },
  statusQrcode: {
    backgroundColor: "rgba(245, 158, 11, 0.1)",
    color: "#D97706",
  },
  statusOpening: {
    backgroundColor: "rgba(59, 130, 246, 0.1)",
    color: "#2563EB",
  },
  statusTimeout: {
    backgroundColor: "rgba(245, 158, 11, 0.1)",
    color: "#D97706",
  },

  // Card footer
  cardFooter: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
    marginTop: "auto",
    paddingTop: 12,
    borderTop:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.04)"
        : "1px solid #F1F5F9",
  },
  cardActionBtn: {
    fontSize: 12,
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    borderRadius: 8,
    textTransform: "none",
    padding: "4px 10px",
  },
  cardIconBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    color: theme.palette.type === "dark" ? "#94A3B8" : "#94A3B8",
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.08)"
          : "#F1F5F9",
    },
  },
  editBtn: {
    "&:hover": {
      backgroundColor: "rgba(59, 130, 246, 0.08)",
      color: "#3B82F6",
    },
  },
  deleteBtn: {
    "&:hover": {
      backgroundColor: "rgba(239, 68, 68, 0.08)",
      color: "#EF4444",
    },
  },
  cardIconBtnSpacer: {
    marginLeft: "auto",
    display: "flex",
    gap: 4,
  },

  // Import status banner
  importBanner: {
    borderRadius: 12,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid #E2E8F0",
    boxShadow: "none",
    marginBottom: 16,
  },
  importBannerContent: {
    padding: "16px 24px !important",
    textAlign: "center",
  },
  importTitle: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
  },
  importSubtitle: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 14,
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    marginTop: 4,
  },

  // Tooltip
  tooltip: {
    backgroundColor: theme.palette.type === "dark" ? "#334155" : "#f8fafc",
    color: theme.palette.type === "dark" ? "#E2E8F0" : "rgba(0, 0, 0, 0.87)",
    fontSize: theme.typography.pxToRem(13),
    fontFamily: '"Inter", "Poppins", sans-serif',
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid #E2E8F0",
    borderRadius: 8,
    maxWidth: 300,
    padding: "8px 12px",
  },
  tooltipPopper: {
    textAlign: "center",
  },
  buttonProgress: {
    color: "#35af9a",
  },

  // Skeleton card
  skeletonCard: {
    borderRadius: 12,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid #E2E8F0",
    boxShadow: "none",
    height: 220,
  },
}));

function CircularProgressWithLabel(props) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="determinate" {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography
          variant="caption"
          component="div"
          color="textSecondary"
        >{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

const CustomToolTip = ({ title, content, children }) => {
  const classes = useStyles();

  return (
    <Tooltip
      arrow
      classes={{
        tooltip: classes.tooltip,
        popper: classes.tooltipPopper,
      }}
      title={
        <React.Fragment>
          <Typography gutterBottom color="inherit" style={{ fontSize: 13, fontWeight: 600 }}>
            {title}
          </Typography>
          {content && <Typography style={{ fontSize: 12 }}>{content}</Typography>}
        </React.Fragment>
      }
    >
      {children}
    </Tooltip>
  );
};

const getChannelIconClass = (channel) => {
  switch (channel) {
    case "facebook":
      return "channelIconFacebook";
    case "instagram":
      return "channelIconInstagram";
    default:
      return "channelIconWhatsapp";
  }
};

const getChannelIcon = (channel) => {
  switch (channel) {
    case "facebook":
      return <Facebook />;
    case "instagram":
      return <Instagram />;
    default:
      return <WhatsApp />;
  }
};

const getStatusChipProps = (status) => {
  switch (status) {
    case "CONNECTED":
      return { label: "Conectado", classKey: "statusConnected" };
    case "DISCONNECTED":
      return { label: "Desconectado", classKey: "statusDisconnected" };
    case "qrcode":
      return { label: "QR Code", classKey: "statusQrcode" };
    case "OPENING":
      return { label: "Conectando...", classKey: "statusOpening" };
    case "TIMEOUT":
    case "PAIRING":
      return { label: "Timeout", classKey: "statusTimeout" };
    default:
      return { label: status || "—", classKey: "statusDisconnected" };
  }
};

const Connections = () => {
  const classes = useStyles();

  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [statusImport, setStatusImport] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const history = useHistory();
  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(
    confirmationModalInitialState
  );
  const [planConfig, setPlanConfig] = useState(false);

  const { user } = useContext(AuthContext);
  const companyId = user.companyId;

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      setPlanConfig(planConfigs);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const responseFacebook = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then((response) => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  const responseInstagram = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          addInstagram: true,
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then((response) => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  useEffect(() => {
    const socket = socketConnection({ companyId, userId: user.id });

    socket.on(`importMessages-${user.companyId}`, (data) => {
      if (data.action === "refresh") {
        setStatusImport([]);
        history.go(0);
      }
      if (data.action === "update") {
        setStatusImport(data.status);
        console.log("Importação concluida com exito", data);
      }
    });

    /* return () => {
      socket.disconnect();
    }; */
  }, [whatsApps]);

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = () => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = React.useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  }, [setSelectedWhatsApp, setWhatsAppModalOpen]);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = React.useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, [setQrModalOpen, setSelectedWhatsApp]);

  const handleEditWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const openInNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId: whatsAppId,
      });
    }

    if (action === "delete") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId: whatsAppId,
      });
    }
    if (action === "closedImported") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.closedImportedTitle"),
        message: i18n.t("connections.confirmationModal.closedImportedMessage"),
        whatsAppId: whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "closedImported") {
      try {
        await api.post(`/closedimported/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.closedimported"));
      } catch (err) {
        toastError(err);
      }
    }

    setConfirmModalInfo(confirmationModalInitialState);
  };

  const renderImportButton = (whatsApp) => {
    if (whatsApp?.statusImportMessages === "renderButtonCloseTickets") {
      return (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          className={classes.cardActionBtn}
          onClick={() => {
            handleOpenConfirmationModal("closedImported", whatsApp.id);
          }}
        >
          {i18n.t("connections.buttons.closedImported")}
        </Button>
      );
    }

    if (whatsApp?.importOldMessages) {
      let isTimeStamp = !isNaN(
        new Date(Math.floor(whatsApp?.statusImportMessages)).getTime()
      );

      if (isTimeStamp) {
        const ultimoStatus = new Date(
          Math.floor(whatsApp?.statusImportMessages)
        ).getTime();
        const dataLimite = +add(ultimoStatus, { seconds: +35 }).getTime();
        if (dataLimite > new Date().getTime()) {
          return (
            <Button
              disabled
              size="small"
              endIcon={
                <CircularProgress
                  size={12}
                  className={classes.buttonProgress}
                />
              }
              variant="outlined"
              color="primary"
              className={classes.cardActionBtn}
            >
              {i18n.t("connections.buttons.preparing")}
            </Button>
          );
        }
      }
    }
  };

  const renderCardActionButtons = (whatsApp) => {
    return (
      <>
        {whatsApp.status === "qrcode" && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            className={classes.cardActionBtn}
            onClick={() => handleOpenQrModal(whatsApp)}
          >
            {i18n.t("connections.buttons.qrcode")}
          </Button>
        )}
        {whatsApp.status === "DISCONNECTED" && (
          <>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              className={classes.cardActionBtn}
              onClick={() => handleStartWhatsAppSession(whatsApp.id)}
            >
              {i18n.t("connections.buttons.tryAgain")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              className={classes.cardActionBtn}
              style={{ color: "#64748B", borderColor: "#E2E8F0" }}
              onClick={() => handleRequestNewQrCode(whatsApp.id)}
            >
              {i18n.t("connections.buttons.newQr")}
            </Button>
          </>
        )}
        {(whatsApp.status === "CONNECTED" ||
          whatsApp.status === "PAIRING" ||
          whatsApp.status === "TIMEOUT") && (
          <>
            <Button
              size="small"
              variant="outlined"
              className={classes.cardActionBtn}
              style={{ color: "#DC2626", borderColor: "rgba(239,68,68,0.3)" }}
              onClick={() => {
                handleOpenConfirmationModal("disconnect", whatsApp.id);
              }}
            >
              {i18n.t("connections.buttons.disconnect")}
            </Button>
            {renderImportButton(whatsApp)}
          </>
        )}
        {whatsApp.status === "OPENING" && (
          <Button
            size="small"
            variant="outlined"
            disabled
            className={classes.cardActionBtn}
          >
            {i18n.t("connections.buttons.connecting")}
          </Button>
        )}
      </>
    );
  };

  const restartWhatsapps = async () => {
    try {
      await api.post(`/whatsapp-restart/`);
      toast.success(i18n.t("connections.waitConnection"));
    } catch (err) {
      toastError(err);
    }
  };

  const renderStatusToolTips = (whatsApp) => {
    return (
      <>
        {whatsApp.status === "DISCONNECTED" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.disconnected.title")}
            content={i18n.t("connections.toolTips.disconnected.content")}
          >
            <SignalCellularConnectedNoInternet0Bar style={{ fontSize: 16, color: "#DC2626" }} />
          </CustomToolTip>
        )}
        {whatsApp.status === "OPENING" && (
          <CircularProgress size={16} className={classes.buttonProgress} />
        )}
        {whatsApp.status === "qrcode" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.qrcode.title")}
            content={i18n.t("connections.toolTips.qrcode.content")}
          >
            <CropFree style={{ fontSize: 16, color: "#D97706" }} />
          </CustomToolTip>
        )}
        {whatsApp.status === "CONNECTED" && (
          <CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
            <SignalCellular4Bar style={{ fontSize: 16, color: green[500] }} />
          </CustomToolTip>
        )}
        {(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.timeout.title")}
            content={i18n.t("connections.toolTips.timeout.content")}
          >
            <SignalCellularConnectedNoInternet2Bar style={{ fontSize: 16, color: "#D97706" }} />
          </CustomToolTip>
        )}
      </>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      <QrcodeModal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
      />
      <WhatsAppModal
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />

      <MainHeader>
        <Title>
          {i18n.t("connections.title")} ({whatsApps.length})
        </Title>
        <MainHeaderButtonsWrapper>
          <PopupState variant="popover" popupId="connections-options-menu">
            {(popupState) => (
              <React.Fragment>
                <Button
                  variant="outlined"
                  className={classes.optionsButton}
                  startIcon={<SettingsOutlined style={{ fontSize: 18 }} />}
                  endIcon={<ArrowDropDown />}
                  {...bindTrigger(popupState)}
                >
                  Opções
                </Button>
                <Menu
                  {...bindMenu(popupState)}
                  className={classes.optionsMenu}
                  getContentAnchorEl={null}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      restartWhatsapps();
                      popupState.close();
                    }}
                  >
                    <ListItemIcon>
                      <RefreshOutlined
                        fontSize="small"
                        style={{ color: "#35af9a" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={i18n.t("connections.restartConnections")}
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      openInNewTab(
                        `https://wa.me/${process.env.REACT_APP_NUMBER_SUPPORT}`
                      );
                      popupState.close();
                    }}
                  >
                    <ListItemIcon>
                      <HeadsetMicOutlined
                        fontSize="small"
                        style={{ color: "#3B82F6" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={i18n.t("connections.callSupport")}
                    />
                  </MenuItem>

                  <Divider style={{ margin: "4px 0" }} />

                  <MenuItem disabled className={classes.menuSectionLabel}>
                    {i18n.t("connections.newConnection")}
                  </MenuItem>

                  {/* WHATSAPP */}
                  <MenuItem
                    disabled={
                      planConfig?.plan?.useWhatsapp ? false : true
                    }
                    onClick={() => {
                      handleOpenWhatsAppModal();
                      popupState.close();
                    }}
                  >
                    <ListItemIcon>
                      <WhatsApp
                        fontSize="small"
                        style={{ color: "#25D366" }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="WhatsApp" />
                  </MenuItem>

                  {/* FACEBOOK */}
                  <FacebookLogin
                    appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                    autoLoad={false}
                    fields="name,email,picture"
                    version="9.0"
                    scope="public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement"
                    callback={responseFacebook}
                    render={(renderProps) => (
                      <MenuItem
                        disabled={
                          planConfig?.plan?.useFacebook ? false : true
                        }
                        onClick={renderProps.onClick}
                      >
                        <ListItemIcon>
                          <Facebook
                            fontSize="small"
                            style={{ color: "#3b5998" }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Facebook" />
                      </MenuItem>
                    )}
                  />

                  {/* INSTAGRAM */}
                  <FacebookLogin
                    appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                    autoLoad={false}
                    fields="name,email,picture"
                    version="9.0"
                    scope="public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement"
                    callback={responseInstagram}
                    render={(renderProps) => (
                      <MenuItem
                        disabled={
                          planConfig?.plan?.useInstagram ? false : true
                        }
                        onClick={renderProps.onClick}
                      >
                        <ListItemIcon>
                          <Instagram
                            fontSize="small"
                            style={{ color: "#e1306c" }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Instagram" />
                      </MenuItem>
                    )}
                  />
                </Menu>
              </React.Fragment>
            )}
          </PopupState>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* Import status banner */}
      {statusImport?.all ? (
        <Card className={classes.importBanner}>
          <CardContent className={classes.importBannerContent}>
            <Typography className={classes.importTitle}>
              {statusImport?.this === -1
                ? i18n.t("connections.buttons.preparing")
                : i18n.t("connections.buttons.importing")}
            </Typography>
            {statusImport?.this === -1 ? (
              <Box mt={1}>
                <CircularProgress size={24} className={classes.buttonProgress} />
              </Box>
            ) : (
              <>
                <Typography className={classes.importSubtitle}>
                  {`${i18n.t("connections.typography.processed")} ${
                    statusImport?.this
                  } ${i18n.t("connections.typography.in")} ${
                    statusImport?.all
                  }  ${i18n.t("connections.typography.date")}: ${
                    statusImport?.date
                  } `}
                </Typography>
                <Box mt={1}>
                  <CircularProgressWithLabel
                    value={(statusImport?.this / statusImport?.all) * 100}
                  />
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* Connection cards grid */}
      <div className={classes.gridContainer}>
        <Grid container spacing={2}>
          {loading
            ? [1, 2, 3].map((n) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={n}>
                  <Card className={classes.skeletonCard}>
                    <CardContent style={{ padding: 20 }}>
                      <Box display="flex" alignItems="center" gridGap={12} mb={2}>
                        <Skeleton variant="rect" width={40} height={40} style={{ borderRadius: 10 }} />
                        <Box flex={1}>
                          <Skeleton variant="text" width="60%" height={20} />
                          <Skeleton variant="text" width="40%" height={16} />
                        </Box>
                      </Box>
                      <Skeleton variant="text" width="80%" height={16} />
                      <Skeleton variant="text" width="60%" height={16} />
                      <Skeleton variant="text" width="70%" height={16} />
                      <Box mt={2}>
                        <Skeleton variant="rect" width="100%" height={32} style={{ borderRadius: 8 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            : whatsApps?.length > 0 &&
              whatsApps.map((whatsApp) => {
                const chipProps = getStatusChipProps(whatsApp.status);
                const channelClass = getChannelIconClass(whatsApp.channel);

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={whatsApp.id}>
                    <Card className={classes.connectionCard}>
                      <CardContent className={classes.cardContent}>
                        {/* Card Header */}
                        <div className={classes.cardHeader}>
                          <div className={classes.cardHeaderLeft}>
                            <div
                              className={`${classes.channelIconWrapper} ${classes[channelClass]}`}
                            >
                              {getChannelIcon(whatsApp.channel)}
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <Typography className={classes.connectionName}>
                                {whatsApp.name}
                              </Typography>
                              <Box display="flex" alignItems="center" gridGap={6} mt={0.5}>
                                <Chip
                                  label={chipProps.label}
                                  size="small"
                                  className={`${classes.statusChip} ${classes[chipProps.classKey]}`}
                                />
                                {renderStatusToolTips(whatsApp)}
                              </Box>
                            </div>
                          </div>
                          {whatsApp.isDefault && (
                            <span className={classes.defaultBadge}>
                              <StarOutlined style={{ fontSize: 12 }} />
                              Padrão
                            </span>
                          )}
                        </div>

                        {/* Card Body */}
                        <div className={classes.cardBody}>
                          <div className={classes.infoRow}>
                            <PhoneAndroid className={classes.infoIcon} />
                            <span className={classes.infoLabel}>Número</span>
                            <span className={classes.infoValue}>
                              {whatsApp.number
                                ? formatSerializedId(whatsApp.number)
                                : "—"}
                            </span>
                          </div>
                          <div className={classes.infoRow}>
                            <Schedule className={classes.infoIcon} />
                            <span className={classes.infoLabel}>Atualizado</span>
                            <span className={classes.infoValue}>
                              {format(
                                parseISO(whatsApp.updatedAt),
                                "dd/MM/yy HH:mm"
                              )}
                            </span>
                          </div>
                        </div>

                        {/* Card Footer */}
                        <div className={classes.cardFooter}>
                          {renderCardActionButtons(whatsApp)}
                          <div className={classes.cardIconBtnSpacer}>
                            <IconButton
                              size="small"
                              className={`${classes.cardIconBtn} ${classes.editBtn}`}
                              onClick={() => handleEditWhatsApp(whatsApp)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              className={`${classes.cardIconBtn} ${classes.deleteBtn}`}
                              onClick={() => {
                                handleOpenConfirmationModal(
                                  "delete",
                                  whatsApp.id
                                );
                              }}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
        </Grid>
      </div>
    </MainContainer>
  );
};

export default Connections;
